import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ArrowLeft, MapPin, Phone, User } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { AppButton } from "@/components/app/AppButton";
import { ROUTES } from "@/lib/routes";
import { OrderSuccessModal } from "@/components/landing/OrderSuccessModal";
import { placeOrder } from "@/api/order";

interface AddressForm {
  fullName: string;
  phone: string;
  pincode: string;
  address: string;
  city: string;
  state: string;
  landmark: string;
}

const INITIAL_FORM: AddressForm = {
  fullName: "",
  phone: "",
  pincode: "",
  address: "",
  city: "",
  state: "",
  landmark: "",
};

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useApp();
  const [form, setForm] = useState<AddressForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<AddressForm>>({});
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [placing, setPlacing] = useState(false);

  const handleChange = (field: keyof AddressForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<AddressForm> = {};
    if (!form.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!form.phone.trim() || form.phone.length !== 10)
      newErrors.phone = "Valid 10-digit phone is required";
    if (!form.pincode.trim() || form.pincode.length !== 6)
      newErrors.pincode = "Valid 6-digit pincode is required";
    if (!form.address.trim()) newErrors.address = "Address is required";
    if (!form.city.trim()) newErrors.city = "City is required";
    if (!form.state.trim()) newErrors.state = "State is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const res = await placeOrder();
      if (res?.data?.redirectUrl) {
        clearCart();
        window.location.href = res.data.redirectUrl;
      } else {
        setOrderSuccess(true);
      }
    } catch {
      setOrderSuccess(true);
    } finally {
      setPlacing(false);
    }
  };

  const handleSuccessClose = () => {
    setOrderSuccess(false);
    clearCart();
    setForm(INITIAL_FORM);
    navigate(ROUTES.PRODUCTS);
  };

  const deliveryCharge = cartTotal >= 999 ? 0 : 99;
  const grandTotal = cartTotal + deliveryCharge;

  if (cart.length === 0 && !orderSuccess) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-5 py-16">
        <div className="text-center">
          <div className="text-6xl mb-4">🛒</div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Your cart is empty
          </h2>
          <p className="text-muted-foreground mb-6">
            Add some beautiful silver jewellery to get started!
          </p>
          <AppButton onClick={() => navigate(ROUTES.PRODUCTS)}>
            Browse Products
          </AppButton>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="min-h-screen py-8 md:py-14">
        <div className="wrapper">
          {/* Back Button */}
          <button
            onClick={() => navigate(ROUTES.PRODUCTS)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            Continue Shopping
          </button>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-8">
            Your Cart
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Cart Items + Address */}
            <div className="lg:col-span-2 space-y-6">
              {/* Cart Items */}
              <div className="bg-background border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">
                    Cart Items ({cart.length})
                  </h2>
                </div>
                <div className="divide-y divide-border">
                  {cart.map(({ product, quantity }) => (
                    <div key={product.id} className="flex gap-4 p-5">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-20 h-20 object-cover rounded-xl shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground text-sm truncate">
                          {product.name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {product.purity} · {product.weight}
                        </p>
                        <div className="flex items-center gap-3 mt-3">
                          {/* Quantity Controls */}
                          <div className="flex items-center border border-border rounded-full overflow-hidden">
                            <button
                              className="px-2.5 py-1 hover:bg-muted transition-colors"
                              onClick={() =>
                                updateQuantity(product.id, quantity - 1)
                              }
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="px-3 text-sm font-semibold">
                              {quantity}
                            </span>
                            <button
                              className="px-2.5 py-1 hover:bg-muted transition-colors"
                              onClick={() =>
                                updateQuantity(product.id, quantity + 1)
                              }
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <button
                            onClick={() => removeFromCart(product.id)}
                            className="p-1.5 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-foreground">
                          ₹{(product.price * quantity).toLocaleString("en-IN")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          ₹{product.price.toLocaleString("en-IN")} each
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Delivery Address */}
              <div className="bg-background border border-border rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  <h2 className="font-semibold text-foreground">
                    Delivery Address
                  </h2>
                </div>
                <div className="p-5 space-y-4">
                  {/* Row 1: Full Name + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your full name"
                        value={form.fullName}
                        onChange={(e) => handleChange("fullName", e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition ${
                          errors.fullName ? "border-destructive" : "border-border"
                        }`}
                      />
                      {errors.fullName && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.fullName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        placeholder="10-digit mobile number"
                        value={form.phone}
                        onChange={(e) =>
                          handleChange(
                            "phone",
                            e.target.value.replace(/\D/g, "").slice(0, 10)
                          )
                        }
                        className={`w-full px-3 py-2.5 border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition ${
                          errors.phone ? "border-destructive" : "border-border"
                        }`}
                      />
                      {errors.phone && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Full Address */}
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1.5 block">
                      Full Address (House No, Street, Area) *
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Enter your complete address"
                      value={form.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      className={`w-full px-3 py-2.5 border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none ${
                        errors.address ? "border-destructive" : "border-border"
                      }`}
                    />
                    {errors.address && (
                      <p className="text-xs text-destructive mt-1">
                        {errors.address}
                      </p>
                    )}
                  </div>

                  {/* Landmark */}
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1.5 block">
                      Landmark (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="Nearby landmark"
                      value={form.landmark}
                      onChange={(e) => handleChange("landmark", e.target.value)}
                      className="w-full px-3 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
                    />
                  </div>

                  {/* Row: City + State + Pincode */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1.5 block">
                        City *
                      </label>
                      <input
                        type="text"
                        placeholder="City"
                        value={form.city}
                        onChange={(e) => handleChange("city", e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition ${
                          errors.city ? "border-destructive" : "border-border"
                        }`}
                      />
                      {errors.city && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.city}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1.5 block">
                        State *
                      </label>
                      <input
                        type="text"
                        placeholder="State"
                        value={form.state}
                        onChange={(e) => handleChange("state", e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition ${
                          errors.state ? "border-destructive" : "border-border"
                        }`}
                      />
                      {errors.state && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.state}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1.5 block">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        placeholder="6-digit pincode"
                        value={form.pincode}
                        onChange={(e) =>
                          handleChange(
                            "pincode",
                            e.target.value.replace(/\D/g, "").slice(0, 6)
                          )
                        }
                        className={`w-full px-3 py-2.5 border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition ${
                          errors.pincode ? "border-destructive" : "border-border"
                        }`}
                      />
                      {errors.pincode && (
                        <p className="text-xs text-destructive mt-1">
                          {errors.pincode}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-background border border-border rounded-2xl overflow-hidden sticky top-24">
                <div className="px-5 py-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">
                    Order Summary
                  </h2>
                </div>
                <div className="p-5 space-y-4">
                  {/* Items */}
                  <div className="space-y-2">
                    {cart.map(({ product, quantity }) => (
                      <div
                        key={product.id}
                        className="flex justify-between text-sm"
                      >
                        <span className="text-muted-foreground truncate mr-2">
                          {product.name} × {quantity}
                        </span>
                        <span className="text-foreground font-medium shrink-0">
                          ₹{(product.price * quantity).toLocaleString("en-IN")}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium">
                        ₹{cartTotal.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Delivery</span>
                      <span
                        className={
                          deliveryCharge === 0
                            ? "text-green-600 font-medium"
                            : "font-medium"
                        }
                      >
                        {deliveryCharge === 0
                          ? "FREE"
                          : `₹${deliveryCharge}`}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between">
                      <span className="font-bold text-foreground">
                        Grand Total
                      </span>
                      <span className="font-bold text-xl text-primary">
                        ₹{grandTotal.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Inclusive of all taxes
                    </p>
                  </div>

                  {deliveryCharge > 0 && (
                    <div className="bg-primary/5 border border-primary/20 rounded-xl px-3 py-2 text-xs text-primary">
                      Add ₹{(999 - cartTotal).toLocaleString("en-IN")} more for
                      FREE delivery!
                    </div>
                  )}

                  <AppButton
                    size="lg"
                    fullWidth
                    loading={placing}
                    onClick={handlePlaceOrder}
                    className="mt-2"
                  >
                    Place Order
                  </AppButton>

                  <p className="text-xs text-center text-muted-foreground">
                    Secured by 256-bit SSL encryption
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <OrderSuccessModal open={orderSuccess} onClose={handleSuccessClose} />
    </>
  );
}
